import { Button, MultiSelect, Text } from "@mantine/core";
import { IconXboxX } from "@tabler/icons-react";
import { IconArrowBackUp } from "@tabler/icons-react";
import {
  setQaQuestionOneCorrect,
  setQaQuestionOneIncorrectParts,
  setQaQuestionTwoCorrect,
  setQaQuestionThreeCorrect,
  RootState,
} from "@/store";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { useMemo } from "react";
import { JobTaskDetailSchema } from "@augmodo/lebron-browser-client";
// The "parts" of question 1 (empty area) the original labeler can get wrong
// are the empty-area question's option labels.
import { EMPTY_AREA_OPTIONS } from "@/config/data";

export const QaQuestionAction = ({
  questionNumber,
  task,
}: {
  questionNumber: number;
  task?: JobTaskDetailSchema;
}) => {
  const dispatch = useAppDispatch();
  const qaResultQuestionOneCorrect = task?.result?.qa_question_one_correct;
  const qaResultQuestionTwoCorrect = task?.result?.qa_question_two_correct;
  const qaResultQuestionThreeCorrect = task?.result?.qa_question_three_correct;
  const hasQaResult =
    typeof qaResultQuestionOneCorrect === "boolean" &&
    typeof qaResultQuestionTwoCorrect === "boolean" &&
    typeof qaResultQuestionThreeCorrect === "boolean";

  const qaQuestionOneCorrect =
    typeof qaResultQuestionOneCorrect === "boolean"
      ? qaResultQuestionOneCorrect
      : useAppSelector((state: RootState) => state.oos.qaQuestionOneCorrect);
  const qaQuestionTwoCorrect =
    typeof qaResultQuestionTwoCorrect === "boolean"
      ? qaResultQuestionTwoCorrect
      : useAppSelector((state: RootState) => state.oos.qaQuestionTwoCorrect);
  const qaQuestionThreeCorrect =
    typeof qaResultQuestionThreeCorrect === "boolean"
      ? qaResultQuestionThreeCorrect
      : useAppSelector((state: RootState) => state.oos.qaQuestionThreeCorrect);

  // Which parts of question 1 were marked wrong. In the active QA flow this
  // comes from redux; in the read-only feedback view it comes from task.result.
  const reduxQuestionOneIncorrectParts = useAppSelector(
    (state: RootState) => state.oos.qaQuestionOneIncorrectParts,
  );
  const questionOneIncorrectParts = hasQaResult
    ? (task?.result?.qa_question_one_incorrect_parts ?? [])
    : reduxQuestionOneIncorrectParts;
  const markedIncorrect = useMemo(() => {
    if (questionNumber === 1) {
      return !qaQuestionOneCorrect;
    } else if (questionNumber === 2) {
      return !qaQuestionTwoCorrect;
    } else if (questionNumber === 3) {
      return !qaQuestionThreeCorrect;
    }
  }, [
    questionNumber,
    qaQuestionOneCorrect,
    qaQuestionTwoCorrect,
    qaQuestionThreeCorrect,
  ]);

  const handleMarkIncorrect = () => {
    if (questionNumber === 1) {
      dispatch(setQaQuestionOneCorrect(!qaQuestionOneCorrect));
    } else if (questionNumber === 2) {
      dispatch(setQaQuestionTwoCorrect(!qaQuestionTwoCorrect));
    } else if (questionNumber === 3) {
      dispatch(setQaQuestionThreeCorrect(!qaQuestionThreeCorrect));
    }
  };

  if (hasQaResult && !markedIncorrect) return null;

  // Show the "which parts were wrong" multiselect only for question 1 once it
  // has been marked incorrect.
  const showQuestionOneParts = questionNumber === 1 && markedIncorrect;

  return (
    <>
      <div
        className="mt-4 flex justify-between items-center"
        style={{
          backgroundColor: "#f0f0f0",
          padding: 10,
          borderRadius: 5,
          border: markedIncorrect ? "3px solid red" : "2px solid transparent",
        }}
      >
        {markedIncorrect && (
          <Text size="lg" fw={600}>
            Incorrect
          </Text>
        )}
        {!markedIncorrect && <Text size="sm">Mark Incorrect</Text>}

        {!hasQaResult && (
          <Button
            variant="transparent"
            onClick={() => {
              handleMarkIncorrect();
            }}
          >
            {markedIncorrect ? (
              <IconArrowBackUp size={18} color="black" />
            ) : (
              <IconXboxX size={35} color="red" />
            )}
          </Button>
        )}
      </div>

      {showQuestionOneParts && (
        <MultiSelect
          className="mt-2"
          label="Which parts were incorrect?"
          placeholder={hasQaResult ? undefined : "Select all that apply"}
          data={EMPTY_AREA_OPTIONS}
          value={questionOneIncorrectParts}
          onChange={(value) => dispatch(setQaQuestionOneIncorrectParts(value))}
          readOnly={hasQaResult}
          clearable={!hasQaResult}
        />
      )}
    </>
  );
};
